import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { supabase } from "../lib/supabase";
import type { Session, User } from "@supabase/supabase-js";
import { identifyUser, resetAnalytics } from "../lib/analytics";
import { setSentryUser } from "../lib/sentry";

// Link / unlink the PostHog identity to the Supabase session (#133).
// Called on every auth-state change: identify when there's a user
// (idempotent — safe on TOKEN_REFRESHED), reset on sign-out so the next
// user isn't conflated. Email is the only PII we attach.
function syncAnalyticsIdentity(sess: Session | null): void {
  if (sess?.user) {
    identifyUser(sess.user.id, { email: sess.user.email });
    setSentryUser(sess.user.id);
  } else {
    resetAnalytics();
    setSentryUser(null);
  }
}

interface AuthContextValue {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  session: null,
  loading: true,
  signOut: async () => {},
});

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setSession(data.session);
      syncAnalyticsIdentity(data.session);
      setLoading(false);
    });
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSession(sess);
      syncAnalyticsIdentity(sess);
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setSession(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user: session?.user ?? null, session, loading, signOut }}>
      {children}
    </AuthContext.Provider>
  );
};

export function useAuth() {
  return useContext(AuthContext);
}
